/**
 * @fileoverview Componente Atom para reproducción de archivos de audio con soporte multi-plataforma y gestión de estados
 * @module AudioMC
 * @category Component - Atom
 * @since 1.0.0
 * @version 3.1.0
 */

import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import useMediaData from "../hooks/shared/data/useMediaData";
import useMediaUI from "../hooks/shared/ui/useMediaUI";
import LoadingMC from "./LoadingMC";
import firefoxAudioWorkaround from "../hooks/shared/ui/firefoxAudioWorkaround";
import IconMC from "./IconMC";
import { getAudioMimeType } from "../../util/audioUtils";
import { isWhatsAppMsPlatform } from "../../services/inbox/inboxMsChatPlatform";

/**
 * @typedef {Object} AudioData
 * @description Estructura de datos del archivo de audio recibido desde la API
 * @property {string} id - Identificador único del archivo de audio
 * @property {string} contentMediaUrl - URL principal del archivo de audio
 */

/**
 * Componente Atom para reproducción de archivos de audio con soporte multi-plataforma.
 * 
 * Maneja la reproducción de archivos de audio desde diferentes plataformas (WhatsApp, Messenger, Instagram)
 * con gestión automática de estados de carga, errores y compatibilidad cross-browser.
 * 
 * @component AudioMC
 * @category Component - Atom
 * 
 * @description
 * **Características:**
 * - Reproducción de audio con controles nativos HTML5
 * - Soporte multi-plataforma (WhatsApp, Messenger, Instagram)
 * - Gestión automática de estados de carga y errores
 * - Workaround específico para Firefox
 * - Internacionalización completa
 * - Optimización de renders con React.memo
 * 
 * **Atomic Design:**
 * - **Nivel**: Atom
 * - **Composición**: Elemento HTML5 audio + componentes LoadingMC e IconMC
 * - **Dependencias**: LoadingMC (atom), IconMC (atom)
 * 
 * **Props Interface:**
 * ```javascript
 * {
 *   data: AudioData,              // Datos del archivo de audio
 *   platform: string,             // Plataforma de origen ('whatsapp'|'messenger'|'instagram')
 *   className?: string,           // Clases CSS adicionales
 *   urlField?: string|string[]    // Campo(s) URL; con lista gana el primero informado
 * }
 * ```
 * 
 * **Arquitectura Optimizada (v3.1.0):**
 * - ✅ **Atom Level**: Componente fundamental para reproducción de audio
 * - ✅ **Hook Composition**: Separación estricta de Data Layer y UI Layer
 * - ✅ **Performance**: React.memo, memoización de handlers
 * - ✅ **Cross-browser**: Workarounds específicos para Firefox
 * - ✅ **Error Handling**: Gestión integral de errores de carga y reproducción
 * - ✅ **Multi-platform**: Soporte para WhatsApp, Messenger, Instagram
 * 
 * **Estados de Componente:**
 * - **loading**: Muestra LoadingMC mientras carga el audio
 * - **error**: Muestra mensaje de error con icono cuando falla la carga
 * - **ready**: Muestra controles de audio nativos cuando está listo
 * 
 * **Flujo de Operación:**
 * 1. **Inicialización**: useMediaData extrae URL del campo especificado
 * 2. **Configuración UI**: useMediaUI configura handlers y estados específicos de plataforma
 * 3. **Carga**: Elemento audio inicia carga automática
 * 4. **Error/Success**: Manejo automático de estados basado en eventos HTML5
 * 5. **Workarounds**: Aplicación de fixes específicos por plataforma si es necesario
 * 
 * @param {Object} props - Propiedades del componente
 * @param {AudioData} props.data - Objeto con datos del archivo de audio desde la API
 * @param {string} props.platform - Plataforma de origen del audio ('whatsapp'|'messenger'|'instagram')
 * @param {string} [props.className] - Clases CSS adicionales para personalizar estilos
 * @param {string|string[]} [props.urlField='contentMediaUrl'] - Campo(s) del objeto data con la URL del audio
 * 
 * @returns {JSX.Element} Elemento JSX que renderiza el reproductor de audio o mensaje de error
 * 
 * @example
 * ```jsx
 * // Uso básico con audio de WhatsApp
 * <AudioMC 
 *   data={{
 *     id: "audio123",
 *     contentMediaUrl: "https://api.example.com/audio.mp3"
 *   }}
 *   platform="whatsapp"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso con estilos personalizados
 * <AudioMC 
 *   data={{
 *     id: "audio456",
 *     contentMediaUrl: "https://api.example.com/audio.wav"
 *   }}
 *   platform="messenger"
 *   className="tw-w-full tw-rounded-lg"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Manejo de audio en Instagram
 * <AudioMC 
 *   data={{
 *     id: "instagram_audio_789",
 *     contentMediaUrl: "https://instagram.com/audio.m4a"
 *   }}
 *   platform="instagram"
 * />
 * ```
 * 
 * @see {@link useMediaData} Hook de Data Layer para gestión de URLs de medios
 * @see {@link useMediaUI} Hook de UI Layer para lógica de interfaz de medios
 * @see {@link LoadingMC} Componente atom para estados de carga
 * @see {@link IconMC} Componente atom para iconografía
 * 
 * @since 1.0.0
 * @version 3.1.0 - Implementación de separación de hooks por layers y optimización de performance
 */
const AudioMC = memo(({ data, platform, className, urlField = 'contentMediaUrl' }) => {
  /**
   * Hook de internacionalización para acceder a las traducciones del módulo Inbox.
   * 
   * @memberof AudioMC
   * @private
   */
  const { t } = useTranslation('InboxMC');

  /**
   * Hook de Data Layer para gestión de URLs y estados de datos del archivo de audio.
   * 
   * Maneja la extracción de URLs desde el objeto data y gestiona los estados
   * relacionados con la carga de datos (loading, error).
   * 
   * @memberof AudioMC
   * @private
   * 
   * @see {@link useMediaData} Para documentación completa del hook
   */
  // Data Layer
  const { url, setUrl, loading, error, setLoading, setError } = useMediaData({ data, urlField });

  /**
   * Hook de UI Layer para gestión de lógica de interfaz específica de medios de audio.
   * 
   * Configura handlers de eventos, claves de React y fuentes de medios optimizadas
   * para diferentes plataformas. Incluye lógica específica para workarounds de Firefox
   * y reintentos de URL en Messenger.
   * 
   * @memberof AudioMC
   * @private
   * 
   * @description
   * **Configuración de Plataforma:**
   * - **WhatsApp**: Aplica firefoxAudioWorkaround en eventos de error
   * - **Messenger**: Obtiene URLs alternativas mediante getFbMsnUrl
   * - **Instagram**: Configuración estándar sin workarounds específicos
   * 
   * **Retornos del Hook:**
   * - `handleMediaError`: Handler optimizado para errores de carga de audio
   * - `audKey`: Clave única para forzar re-render del elemento audio
   * - `audSrc`: URL procesada y optimizada para reproducción
   * 
   * @see {@link useMediaUI} Para documentación completa del hook
   * @see {@link firefoxAudioWorkaround} Para detalles del workaround de Firefox
   */
  // UI Layer: lógica genérica de media
  const {
    handleMediaError,
    mediaKey: audKey,
    mediaSrc: audSrc,
  } = useMediaUI({
    platform,
    url,
    data,
    setUrl,
    setError,
    setLoading,
    mediaType: 'audio',
    onPlatformRetry: async (platform, { event, getFbMsnUrl }) => {
      if (isWhatsAppMsPlatform(platform)) {
        firefoxAudioWorkaround(event);
      }
      if (platform === 'messenger') {
        const resultUrl = await getFbMsnUrl();
        setUrl(resultUrl);
      }
    }
  });

  // fileName y contentMediaMimeType describen el archivo entregado. Cuando el
  // reproductor tomó la copia (internalUrl: el m4a de una nota de voz que salió
  // en Ogg/Opus), esas pistas la etiquetarían como audio/ogg y Safari descartaría
  // el <source> sin intentarlo. La copia se tipa por su propia URL.
  const playingPlaybackCopy = Boolean(data?.internalUrl) && url === data.internalUrl;
  const audioMimeType = getAudioMimeType(audSrc, playingPlaybackCopy ? {} : {
    fileName: data?.fileName || data?.filename,
    mimeType: data?.contentMediaMimeType || data?.content_media_mime_type
  });

  return (
    <>
      <LoadingMC loading={loading} />
        {error ? (
          <blockquote className="tw-text-center tw-text-sm tw-flex tw-items-center tw-justify-start tw-gap-2">
            <IconMC name="volume-mute-fill" />
            {t('audio.not_available')}
          </blockquote>
        ) : (
          <audio
            controls
            key={audKey}
            className={`${className} ${loading ? 'tw-hidden' : ''}`}
            onError={handleMediaError}
            onLoadedData={() => setLoading(false)}
          >
            <source src={audSrc} {...(audioMimeType ? { type: audioMimeType } : {})} />
          </audio>
        )}
    </>
  );
});

AudioMC.displayName = "AudioMC";

export default AudioMC;
